import { HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as _ from "lodash";
import { Observable, range } from "rxjs";
import { catchError, map, mergeMap, switchMap, toArray } from "rxjs/operators";

import { environment } from "../../environments/environment";

import { AuthHttpService } from "../core/auth-http.service";
import { FederatedIdentityGroup } from "./federated-identity-group";
import { Hospital } from "../hospitals/hospital";

export type HospitalSortField = "name" | "created_at" | "updated_at";
export type SortType = "ASC" | "DESC";

export interface HospitalsQueryParams {
  page?: number;
  items?: number;
  sortField?: HospitalSortField;
  sortType?: SortType;
}

/**
 * Pagination metadata the v3 API returns as response headers (Pagy) rather
 * than in the body.
 */
export interface PaginationMeta {
  currentPage: number;
  pageItems: number;
  totalCount: number;
  totalPages: number;
}

export interface HospitalsResponse {
  hospitals: Hospital[];
}

export interface PaginatedHospitals extends HospitalsResponse {
  pagination: PaginationMeta;
}

/** Pagy max_limit — larger values are clamped server-side. */
export const MAX_PAGE_ITEMS = 100;

/** Cap on simultaneous page requests when eager loading a collection. */
export const PAGE_FETCH_CONCURRENCY = 5;

@Injectable()
export class FederatedIdentityGroupService {
  constructor(private http: AuthHttpService) {}

  create(
    customerId,
    federatedIdentityGroup: FederatedIdentityGroup,
  ): Observable<FederatedIdentityGroup | string> {
    const url = `${environment.apiv3url}/federated_identity_groups`;
    const payLoad = {
      group_name: federatedIdentityGroup.group_name,
      description: federatedIdentityGroup.description,
      limited_access: federatedIdentityGroup.limited_access,
      customer_id: customerId,
    };

    return this.http
      .postV3<FederatedIdentityGroup>(url, payLoad)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  delete(id): Observable<any> {
    let url = `${environment.apiv3url}/federated_identity_groups/${id}`;

    return this.http
      .deleteV3(url)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  getFederatedIdentityGroup(id): Observable<any> {
    let url = `${environment.apiv3url}/federated_identity_groups/${id}`;

    return this.http
      .getV3<FederatedIdentityGroup>(url)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  /**
   * Fetch a single page of hospitals for a customer, along with the pagination
   * metadata reported in the response headers.
   */
  getHospitals(
    customerId,
    params: HospitalsQueryParams = {},
  ): Observable<PaginatedHospitals> {
    const url = `${environment.apiv3url}/hospitals`;
    let httpParams = new HttpParams()
      .set("customer_id", customerId)
      .set("include_children", true);

    if (params.page !== undefined) {
      httpParams = httpParams.set("page", Math.max(Math.floor(params.page), 1));
    }
    if (params.items !== undefined) {
      httpParams = httpParams.set(
        "items",
        Math.min(Math.max(Math.floor(params.items), 1), MAX_PAGE_ITEMS),
      );
    }
    if (params.sortField) {
      httpParams = httpParams.set("sort_field", params.sortField);
    }
    if (params.sortType) {
      httpParams = httpParams.set("sort_type", params.sortType);
    }

    return this.http
      .getV3Response<HospitalsResponse>(url, httpParams)
      .pipe(
        map((response) => ({
          hospitals: response.body?.hospitals ?? [],
          pagination: this.readPaginationMeta(response.headers),
        })),
        catchError((err) => this.http.handleError(err)),
      ) as Observable<PaginatedHospitals>;
  }

  /**
   * Fetch every hospital for a customer by walking the paginated endpoint.
   * Page 1 is requested first to learn the page count, then the remaining
   * pages are fetched with at most PAGE_FETCH_CONCURRENCY requests in flight.
   * Errors on any page fail the whole load.
   */
  getAllHospitals(
    customerId,
    params: Omit<HospitalsQueryParams, "page" | "items"> = {},
  ): Observable<HospitalsResponse> {
    // A stable sort keeps page boundaries consistent across parallel requests.
    const query: HospitalsQueryParams = {
      sortField: "name",
      sortType: "ASC",
      ...params,
      items: MAX_PAGE_ITEMS,
    };

    return this.getHospitals(customerId, { ...query, page: 1 }).pipe(
      switchMap((firstPage) =>
        range(2, Math.max(firstPage.pagination.totalPages - 1, 0)).pipe(
          mergeMap(
            (page) =>
              this.getHospitals(customerId, { ...query, page }).pipe(
                map((result) => ({ page, hospitals: result.hospitals })),
              ),
            PAGE_FETCH_CONCURRENCY,
          ),
          toArray(),
          map((pages) => ({
            hospitals: [
              ...firstPage.hospitals,
              ..._.flatMap(_.sortBy(pages, "page"), "hospitals"),
            ],
          })),
        ),
      ),
    );
  }

  private readPaginationMeta(headers: HttpHeaders): PaginationMeta {
    const read = (name: string, fallback: number) =>
      Number(headers.get(name)) || fallback;

    return {
      currentPage: read("Current-Page", 1),
      pageItems: read("Page-Items", MAX_PAGE_ITEMS),
      totalCount: read("Total-Count", 0),
      totalPages: read("Total-Pages", 1),
    };
  }

  getRoles(): Observable<any> {
    let url = `${environment.apiv3url}/roles`;

    return this.http
      .getV3(url)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  getCustomerGroups(customerId?: string): Observable<any> {
    let url = `${environment.apiv3url}/federated_identity_groups?customer_id=${customerId}`;

    return this.http
      .getV3(url)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  update(id?: string, payload?: any): Observable<any> {
    let url = `${environment.apiv3url}/federated_identity_groups/${id}`;

    return this.http
      .updateV3(url, payload)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  createGroupHospitals(groupId, hospitals): Observable<any>[] {
    const url = `${environment.apiv3url}/federated_identity_group_hospitals`;

    const observableArray = [];
    hospitals.forEach((hospital) => {
      let body = {
        federated_identity_group_hospital: {
          federated_identity_group_id: groupId,
          hospital_id: hospital.id,
        },
      };

      observableArray.push(
        this.http
          .postV3(url, body)
          .pipe(catchError((err) => this.http.handleError(err))),
      );
    });

    return observableArray;
  }

  removeGroupHospitals(groupId, hospitals): Observable<any>[] {
    const baseUrl = `${environment.apiv3url}/federated_identity_group_hospitals/${groupId}?federated_identity_group_id=${groupId}&hospital_id=`;

    const observableArray = [];
    hospitals.forEach((hospital) => {
      observableArray.push(
        this.http
          .deleteV3(baseUrl + hospital.id)
          .pipe(catchError((err) => this.http.handleError(err))),
      );
    });

    return observableArray;
  }

  getGroupHospitals(groupId): Observable<any> {
    let url = `${environment.apiv3url}/federated_identity_group_hospitals?federated_identity_group_id=${groupId}`;

    return this.http
      .getV3(url)
      .pipe(catchError((err) => this.http.handleError(err)));
  }

  createGroupRoles(groupId, roles): Observable<any>[] {
    const url = `${environment.apiv3url}/federated_identity_group_roles`;

    const observableArray = [];
    roles.forEach((role) => {
      let body = {
        federated_identity_group_role: {
          federated_identity_group_id: groupId,
          role_id: role.id,
        },
      };

      observableArray.push(
        this.http
          .postV3(url, body)
          .pipe(catchError((err) => this.http.handleError(err))),
      );
    });

    return observableArray;
  }

  removeGroupRoles(groupId, roles): Observable<any>[] {
    const baseUrl = `${environment.apiv3url}/federated_identity_group_roles/${groupId}?federated_identity_group_id=${groupId}&role_id=`;

    const observableArray = [];
    roles.forEach((role) => {
      observableArray.push(
        this.http
          .deleteV3(baseUrl + role.id)
          .pipe(catchError((err) => this.http.handleError(err))),
      );
    });

    return observableArray;
  }

  getGroupRoles(groupId): Observable<any> {
    let url = `${environment.apiv3url}/federated_identity_group_roles?federated_identity_group_id=${groupId}`;

    return this.http
      .getV3(url)
      .pipe(catchError((err) => this.http.handleError(err)));
  }
}
